import { h } from '@/lib/dom';
import { SkeletonList } from '@/components/Skeleton';
import { SparklineRow } from '@/components/SparklineRow';
import { Sparkline } from '@/components/Sparkline';
import { data } from '@/lib/data';
import { formatDate, plural } from '@/lib/format';
import { currentSession } from '@/lib/session';
import type { RouteContext } from '@/router';
import { navigate, registerCleanup } from '@/router';
import { pageLayout } from './layout';
import type { MarkerOverviewItem, OverviewPanel } from '@/api/types';

export const PANEL_LABELS: Record<string, string> = {
  lipid:         'Липиды',
  thyroid:       'Щитовидка',
  cmp:           'Общий биохимический',
  cbc:           'Общий анализ крови',
  vitamins:      'Витамины и минералы',
  inflammation:  'Воспаление',
  other:         'Прочие',
  biochem:       'Биохимия',
  hormones:      'Гормоны',
  metabolic:     'Углеводный обмен',
  minerals:      'Микроэлементы и электролиты',
  urine:         'Моча',
  tumor_markers: 'Онкомаркёры',
};

export async function renderLabsOverview(_ctx: RouteContext): Promise<Node> {
  const user = currentSession();
  if (!user) {
    navigate('/login', { replace: true });
    return h('div');
  }
  const isOwner = user.role === 'owner';

  const searchInput = h('input', {
    class: 'form-input labs-search',
    type: 'search',
    placeholder: 'Поиск маркёра',
    aria: { label: 'Поиск маркёра' },
  }) as HTMLInputElement;

  const subtitleEl = h('p', { class: 'labs-subtitle', id: 'labs-subtitle' }, 'Загружаем…');

  const contentEl = h('div', { id: 'labs-content' }, SkeletonList(6));

  const header = h('div', { class: 'labs-header' }, [
    h('div', { class: 'labs-header-left' }, [
      h('h1', { class: 'labs-title' }, 'Анализы'),
      subtitleEl,
    ]),
    h('div', { class: 'labs-header-right' }, [
      isOwner
        ? h('a', { href: '/labs/upload', class: 'btn btn-secondary btn-sm' }, '+ Загрузить анализ')
        : null,
    ]),
  ]);

  const searchRow = h('div', { class: 'labs-search-row' }, [searchInput]);

  const root = pageLayout({
    user,
    active: 'labs',
    children: [
      header,
      searchRow,
      contentEl,
    ],
  });

  void (async () => {
    try {
      const overview = await data.getOverview();
      // Bail if the user navigated away mid-fetch — otherwise the observer +
      // cleanup register into the next view's generation over detached nodes.
      if (!root.isConnected) return;
      const subtitle = root.querySelector('#labs-subtitle');
      if (subtitle) {
        const totalMarkers = overview.panels.reduce((n, p) => n + p.items.length, 0);
        subtitle.textContent = overview.last_lab_drawn_at
          ? `Последний забор ${formatDate(overview.last_lab_drawn_at)} · ${totalMarkers} ${plural(totalMarkers, ['маркёр', 'маркёра', 'маркёров'])}`
          : 'Ещё нет ни одного анализа';
      }
      contentEl.innerHTML = '';
      if (!overview.panels.length) {
        contentEl.appendChild(h('div', { class: 'empty-state' }, [
          h('p', null, 'Анализов ещё нет.'),
          isOwner
            ? h('a', { href: '/labs/upload', class: 'btn btn-primary mt-2' }, 'Загрузить первый анализ')
            : h('p', { class: 'text-tertiary' }, 'Владелец загрузит их здесь.'),
        ]));
        return;
      }
      const panels = panelsView(overview.panels, overview.last_lab_drawn_at);
      contentEl.appendChild(panels);

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const placeholder = entry.target as HTMLElement;
            const values: Array<number | null> = JSON.parse(placeholder.dataset['values'] ?? '[]');
            const color = placeholder.dataset['color'] ?? 'var(--chart-line-primary)';
            const svg = Sparkline({ values, color, width: 100, height: 18 });
            placeholder.replaceWith(svg);
            observer.unobserve(placeholder);
          });
        },
        { rootMargin: '200px 0px' },
      );
      panels.querySelectorAll<HTMLElement>('.spark-placeholder').forEach((el) => observer.observe(el));
      registerCleanup(() => observer.disconnect());

      // Tell the router that real content is in the DOM — it will restore
      // a saved scroll position now that the page has its final height.
      document.dispatchEvent(new Event('route:settled'));

      const filterRows = (q: string) => {
        panels.querySelectorAll<HTMLElement>('[data-marker-name]').forEach((row) => {
          const name = row.dataset['markerName'] ?? '';
          row.style.display = !q || name.toLowerCase().includes(q) ? '' : 'none';
        });
        // Hide panels where all rows are hidden
        panels.querySelectorAll<HTMLElement>('.labs-panel').forEach((panel) => {
          const rows = panel.querySelectorAll<HTMLElement>('[data-marker-name]');
          const allHidden = Array.from(rows).every((r) => r.style.display === 'none');
          panel.style.display = allHidden ? 'none' : '';
        });
      };

      searchInput.addEventListener('input', () => {
        filterRows(searchInput.value.trim().toLowerCase());
      });
    } catch (err) {
      contentEl.innerHTML = '';
      contentEl.appendChild(h('div', { class: 'empty-state', role: 'alert' }, [
        h('p', null, 'Не удалось загрузить анализы.'),
        h('button', {
          class: 'btn btn-secondary mt-2',
          type: 'button',
          on: { click: () => navigate('/labs', { replace: true }) },
        }, 'Повторить'),
      ]));
      console.error(err);
    }
  })();

  return root;
}

function panelsView(panels: OverviewPanel[], lastDrawnAt: string | null): HTMLElement {
  const wrapper = h('div', { class: 'labs-panels' });
  const cutoff = staleCutoff();
  const ordered = [...panels]
    .sort((a, b) => outOfRangeCount(b.items, cutoff) - outOfRangeCount(a.items, cutoff));
  for (const panel of ordered) {
    const label = PANEL_LABELS[panel.panel] ?? panel.panel;
    const out = outOfRangeCount(panel.items, cutoff);

    const countEl = out > 0
      ? h('span', { class: 'labs-panel-count has-oor' },
          `${out} ${plural(out, ['● отклонение', '● отклонения', '● отклонений'])}`)
      : h('span', { class: 'labs-panel-count norm' }, 'норма');

    const panelHeader = h('div', { class: 'labs-panel-header' }, [
      h('h2', { class: 'labs-panel-title' }, label),
      countEl,
    ]);

    // Sort: non-stale out-of-range наверх; всё остальное (включая stale) —
    // ниже, в порядке алфавита по display_ru.
    const rows = panel.items
      .slice()
      .sort((a, b) => {
        const aOor = !isStale(a, cutoff) && outOfRange(a.last_flag);
        const bOor = !isStale(b, cutoff) && outOfRange(b.last_flag);
        if (aOor !== bOor) return aOor ? -1 : 1;
        return a.display_ru.localeCompare(b.display_ru, 'ru');
      })
      .map((item) => renderRow(item, lastDrawnAt, cutoff));

    const panelEl = h('div', { class: 'labs-panel' }, [panelHeader, ...rows]);
    wrapper.appendChild(panelEl);
  }
  return wrapper;
}

// Маркёр считается «устаревшим» в overview, если последний забор раньше
// 1 января прошлого календарного года. Исторические CSV-импорты
// подсвечивались как отклонения в списке `/labs`, хотя данные не
// релевантны клинически. Устаревшие — серый бейдж, не учитываются в
// счётчике панели и не приоритизируются в сортировке (идут в общем
// алфавитном порядке). На `/labs/:key` оригинальный флаг сохраняется.
function staleCutoff(): string {
  const now = new Date();
  return `${now.getFullYear() - 1}-01-01`;
}

function isStale(item: MarkerOverviewItem, cutoff: string): boolean {
  return !item.last_drawn_at || item.last_drawn_at < cutoff;
}

function outOfRangeCount(items: MarkerOverviewItem[], cutoff: string): number {
  return items.filter((i) => !isStale(i, cutoff) && outOfRange(i.last_flag)).length;
}

function outOfRange(flag: string | null | undefined): boolean {
  return flag === 'H' || flag === 'L' || flag === 'HH' || flag === 'LL';
}

function renderRow(item: MarkerOverviewItem, _lastDrawn: string | null, cutoff: string): HTMLElement {
  const stale = isStale(item, cutoff);
  const row = SparklineRow({
    canonicalKey: item.canonical_key,
    name: item.display_ru,
    value: item.last_value,
    unit: item.unit,
    delta: computeDelta(item.sparkline),
    flag: item.last_flag,
    status: stale ? 'stale' : undefined,
    sparkline: item.sparkline,
    drawnAt: item.last_drawn_at,
  });
  (row as HTMLElement).dataset['markerName'] = `${item.display_ru} ${item.display_en} ${item.canonical_key}`;
  return row;
}

// Разница отдаётся сырой. Округление до 1e-3 стояло здесь от старого formatNumber
// с двумя знаками; после перехода строки на formatLabValue оно только съедало смысл:
// у марганца 0.0017 против 0.0015 разница схлопывалась в ноль ещё до форматирования,
// и строка показывала «0 —», то есть «не менялось». Хвостовые нули и float-шум
// срезает formatLabValue.
function computeDelta(sparkline: Array<number | null>): number | null {
  const valid = sparkline.filter((v): v is number => v != null && Number.isFinite(v));
  if (valid.length < 2) return null;
  return valid[valid.length - 1]! - valid[valid.length - 2]!;
}
